import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { SettingRow } from '@solum/sdk'
import { useSolum } from '../../data/client'
import { queryState } from '../../data/QueryBoundary'

export interface SettingsDestination {
  key: string
  label: string
  to: string
}

const MODULE_LABEL: Record<string, string> = { ai: 'In-context copilot (AI)', crm: 'CRM' }

export const PLATFORM_SETTINGS: readonly SettingsDestination[] = [
  { key: 'branding', label: 'Branding', to: '/settings?tab=branding' },
  { key: 'regional', label: 'Regional & currency', to: '/settings?tab=regional' },
  { key: 'entities', label: 'Entities & fields', to: '/settings?tab=entities' },
  { key: 'licence', label: 'Licence', to: '/settings?tab=licence' },
  { key: 'deployment', label: 'Deployment', to: '/settings?tab=deployment' },
  { key: 'relationships', label: 'Relationships', to: '/settings?tab=relationships' },
  { key: 'party-roles', label: 'Party roles', to: '/settings?tab=party-roles' },
  { key: 'legal-entities', label: 'Legal entities', to: '/settings?tab=legal-entities' },
]

export function settingModuleLabel(module: string): string {
  return MODULE_LABEL[module] ?? module.replaceAll('-', ' ').replace(/\b\w/g, (letter) => letter.toLocaleUpperCase())
}

export function settingsDestinations(modules: readonly string[], demo = false): SettingsDestination[] {
  return [
    ...PLATFORM_SETTINGS,
    ...[...modules].sort().map((module) => ({
      key: `module-${module}`,
      label: settingModuleLabel(module),
      to: `/settings?tab=${encodeURIComponent(module)}`,
    })),
    ...(demo ? [{ key: 'sandbox', label: 'Sandbox', to: '/settings?tab=sandbox' }] : []),
  ]
}

/*
 * ── `SettingsNavGroup`, `settingsNavGroups` AND `settingsPageNavGroups` ARE
 *    GONE (#2328 §5, ROOT ruling 74071 §3/§6) ─────────────────────────────
 *
 * They existed to solve a problem that no longer exists. #1680's defect was TWO
 * settings navigation landmarks on one screen — the shell's Settings group and
 * the settings page's inner rail, both rendering `settingsDestinations(...)` —
 * and `settingsPageNavGroups(shell, …)` fixed it by SUBTRACTING one list from
 * the other, so the two could not drift.
 *
 * The Settings Studio is now the only settings navigation in the console: the
 * shell's Settings group is gone (`Nav.tsx`), the Platform group with it, and
 * the Studio's rail is drawn from the registry index. There is nothing left to
 * subtract from. Keeping a subtraction whose first argument is always `[]`
 * would be a mechanism that reads as a control and proves nothing.
 *
 * `settingsDestinations` STAYS, and is the point: it is still the authority for
 * which `/settings?tab=` destinations this deployment has, and `settings/pages.ts`
 * derives the registry's page set from it. The authority survived; the second
 * renderer did not.
 */

/** One deployment-derived list shared by the Settings page and its navigation. */
export function useSettingModules(): { modules: string[]; ready: boolean } {
  const { client } = useSolum()
  const query = useQuery({ queryKey: ['settings', 'all'], queryFn: () => client.describeSettings() })
  const modules = useMemo(() => {
    const rows = (query.data ?? []) as SettingRow[]
    return [...new Set(rows.map((row) => row.module))].sort()
  }, [query.data])
  return { modules, ready: queryState([query]).kind === 'ready' }
}
